/* eslint-disable react/default-props-match-prop-types */
import React, { useContext } from 'react';

import { popupManager } from './popup-manager';
import type { IPopupContextInjectedProps, IPopupManager, IPopupSettings } from './types';

export const PopupContext = React.createContext(popupManager);

type PopupContextProviderProps = React.PropsWithChildren<IPopupSettings>;

export const PopupContextProvider: React.FC<PopupContextProviderProps> = (props: PopupContextProviderProps) => {
  if (props.isGateEnabled) {
    popupManager.setGate();
  }

  if (props.timeout) {
    popupManager.setTimer(props.timeout);
  }

  if (props.logger) {
    popupManager.setLogger(props.logger);
  }

  return (
    <PopupContext.Provider value={popupManager}>
      {props.children}
    </PopupContext.Provider>
  );
};

PopupContextProvider.defaultProps = {
  isGateEnabled: false,
  timeout: null,
  permissions: {},
  logger: null,
};

export function withPopupProvider<P extends object>(Component: React.ComponentType<P>): React.ComponentType<React.PropsWithChildren<P & IPopupSettings>> {
  return class PopupProvider extends React.PureComponent<React.PropsWithChildren<P & IPopupSettings>> {
    static defaultProps: Partial<React.PropsWithChildren<P & IPopupSettings>> = {
      isGateEnabled: false,
      timeout: null,
      permissions: {},
      logger: null,
    } as Partial<React.PropsWithChildren<P & IPopupSettings>>;

    render() {
      if (this.props.isGateEnabled) {
        popupManager.setGate();
      }

      if (this.props.timeout) {
        popupManager.setTimer(this.props.timeout);
      }

      if (this.props.logger) {
        popupManager.setLogger(this.props.logger);
      }

      return (
        <PopupContext.Provider value={popupManager}>
          <Component {...(this.props as P)} />
        </PopupContext.Provider>
      );
    }
  };
}

export const withPopupContext = <P extends IPopupContextInjectedProps>(
  WrappedComponent: React.ComponentType<P>
): React.FC<Omit<P, keyof IPopupContextInjectedProps>> => (
    (props: Omit<P, keyof IPopupContextInjectedProps>) => (
      <PopupContext.Consumer>
        {(val: IPopupManager) => (<WrappedComponent
          {...(props as P)}
          popupManager={val}
        />)}
      </PopupContext.Consumer>
    )
  );

export const usePopupContext = () => useContext(PopupContext);
